import { getDeliveryByEventLabel, getProductIdFromProductCode } from '@noths/polaris-client-utils';
import { RequireKeys } from '@noths/polaris-dev-ts-recipes';
import { BaseFieldObject, Price, Product, SimilarProduct } from '@noths/polaris-dev-ts-types';

import { PageType } from 'src/types';
import { ImpressionFieldObject, ProductFieldObject } from 'src/types/googleAnalytics';
import { isCategoryPageGuard, isPartnerPageGuard, isSearchPageGuard } from 'src/utils';
import { ReduxApplicationState } from '../redux/combinedReducer';

type FieldObjectType<P extends PageType> = ProductFieldObject<P> | ImpressionFieldObject<P>;
type StateProp = RequireKeys<Partial<ReduxApplicationState>, 'navigation' | 'products'>;

const getPriceInGBP = (prices: Price[]) => {
  const priceInGBP = prices.find(({ currency }) => currency === 'GBP');

  return priceInGBP?.amount
    ? new Intl.NumberFormat('en-GB', {
        style: 'decimal',
        minimumFractionDigits: 2,
      }).format(priceInGBP.amount / 100)
    : '';
};

const generateFieldObject = (
  product: Product,
  state: StateProp,
  positionOverride?: number,
): BaseFieldObject => {
  const {
    purchasable,
    code,
    deliveryByEvent,
    freeDomesticDelivery,
    isNew,
    onSale,
    partner,
    price,
    salePercentage,
    title,
  } = product;
  const { items, productsReturnedCount } = state.products;
  const findProductByCode = (prod: Product) => prod.code === product.code;
  const products = Object.values(items).flat();
  const pageProductPosition = (products.findIndex(findProductByCode) % productsReturnedCount) + 1;

  const findProductWithinPage = (pageNumber: string) => items[pageNumber].find(findProductByCode);
  const pages = Object.keys(items);
  const pageNumber = Number(pages.find(findProductWithinPage));

  /*
    Use page product position if on the 1st page.
    Otherwise, count number of products spanning all previous pages,
    and add page product position to said number.
  */
  const dimension25 =
    pageNumber === 1
      ? pageProductPosition
      : pageProductPosition + (pageNumber - 1) * productsReturnedCount;

  const dimension22List: string[] = [
    freeDomesticDelivery && 'free uk delivery',
    isNew && 'new',
    onSale && !!salePercentage && `-${salePercentage}% off`,
    !purchasable && 'out of stock',
    deliveryByEvent?.arriveByEvent && getDeliveryByEventLabel(deliveryByEvent.eventName),
  ].filter(Boolean) as string[];

  return {
    brand: partner.name,
    dimension22: dimension22List.join(','),
    dimension25: positionOverride ?? dimension25,
    dimension30: String(code),
    dimension31: String(price.amount / 100),
    dimension32: String(price.amount / 100),
    dimension33: onSale ? `${salePercentage}.0%` : 'none',
    dimension34: partner.shortcode,
    id: String(getProductIdFromProductCode(code)),
    name: title,
    position: positionOverride ?? pageProductPosition,
    price: String(price.amount / 100),
  };
};

const getFieldObject = <P extends PageType = PageType>(
  product: Product,
  state: StateProp,
  positionOverride?: number,
): FieldObjectType<P> =>
  generateFieldObject(product, state, positionOverride) as FieldObjectType<P>;

const getDimension16PartialFieldObject = (
  fieldObject: Partial<BaseFieldObject>,
  product: Product,
  state: StateProp,
) => {
  const {
    navigation: { pageType },
    products: { category },
  } = state;

  const fieldObj: Partial<BaseFieldObject> = {};

  if (isCategoryPageGuard<FieldObjectType<PageType.Category>>(pageType!, fieldObject)) {
    fieldObj.dimension16 = String(category!.id);
  } else if (isPartnerPageGuard<FieldObjectType<PageType.Partner>>(pageType!, fieldObject)) {
    fieldObj.dimension16 = product.partner.name.toLowerCase();
  } else if (isSearchPageGuard<FieldObjectType<PageType.Search>>(pageType!, fieldObject)) {
    fieldObj.dimension16 = '15000';
  }

  return fieldObj;
};

export const getProductFieldObject = <T extends PageType>(
  product: Product,
  state: StateProp,
  positionOverride?: number,
): ProductFieldObject<T> => {
  const fieldObject = getFieldObject(product, state, positionOverride);

  const dimension16PartialFieldObject = getDimension16PartialFieldObject(
    fieldObject,
    product,
    state,
  ) as ProductFieldObject<T>;

  return {
    ...fieldObject,
    ...dimension16PartialFieldObject,
  } as ProductFieldObject<T>;
};

const getFieldObjectList = (product: Product, state: StateProp, listSuffix?: ` - ${string}`) => {
  const {
    navigation: { pageType },
    products,
    products: { category },
  } = state;

  const optionalSponsoredSuffix = product.isSponsored ? ' - sponsored' : '';
  const suffix = listSuffix ?? optionalSponsoredSuffix;

  switch (pageType) {
    case PageType.Category:
      return `${PageType.Category} - ${category!.name}${suffix}`;
    case PageType.Partner:
      return `${PageType.Partner} - ${product.partner.name.toLowerCase()}${suffix}`;
    case PageType.Search:
      return `${PageType.Search} - ${products.searchTerm}${suffix}`;
    default:
      return '';
  }
};

export const getImpressionFieldObject = <T extends PageType>(
  product: Product,
  state: StateProp,
  positionOverride?: number,
  listSuffix?: ` - ${string}`,
) => {
  const impressionFieldObject = {
    ...getFieldObject(product, state, positionOverride),
    list: getFieldObjectList(product, state, listSuffix),
  };

  const dimension16PartialFieldObject = getDimension16PartialFieldObject(
    impressionFieldObject,
    product,
    state,
  ) as ImpressionFieldObject<T>;

  return {
    ...impressionFieldObject,
    ...dimension16PartialFieldObject,
  } as ImpressionFieldObject<T>;
};

interface GenerateProductsEventParams {
  products: SimilarProduct[];
  placementName?: string;
  placementStrategy?: string;
  positionInList?: number;
}

export const generateProductsForEvent = ({
  products,
  placementName,
  placementStrategy,
  positionInList,
}: GenerateProductsEventParams): BaseFieldObject[] =>
  products.map(
    (
      {
        free_domestic_delivery,
        new: isNew,
        on_sale,
        purchasable,
        sale_percentage,
        prices,
        title,
        code,
        partner,
      },
      index,
    ) => {
      const dimension22List = [
        free_domestic_delivery && 'free uk delivery',
        isNew && 'new',
        on_sale && !!sale_percentage && `-${sale_percentage}% off`,
        !purchasable && 'out of stock',
      ]
        .filter(Boolean)
        .join(', ');

      const price = getPriceInGBP(prices);
      const position = positionInList || index + 1;
      const list = placementStrategy
        ? `product - rich relevance - ${placementName} - ${placementStrategy}`
        : `product - rich relevance - ${placementName}`;

      const baseFieldObject: BaseFieldObject = {
        name: title,
        id: String(getProductIdFromProductCode(code)),
        dimension22: dimension22List,
        dimension25: position,
        dimension30: String(code),
        dimension31: price,
        price: price,
        dimension32: price,
        dimension33: on_sale ? `${sale_percentage}.0%` : 'none',
        brand: partner.name,
        dimension34: partner.shortcode,
        position: position,
        list,
        dimension16: 'not set',
      };

      return baseFieldObject;
    },
  );
